import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarDays, LayoutGrid, List } from "lucide-react";
import { addDays, format } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScheduleViewSwitcher } from "@/components/schedule/ScheduleViewSwitcher";
import { ScheduleDateNavigation } from "@/components/schedule/ScheduleDateNavigation";
import { ScheduleTimelineCard } from "@/components/schedule/ScheduleTimelineCard";
import { WeeklyScheduleGrid } from "@/components/schedule/weekly/WeeklyScheduleGrid";
import { AthleteDayList } from "@/components/schedule/weekly/AthleteDayList";
import { WeeklyAgendaList } from "@/components/schedule/weekly/WeeklyAgendaList";
import { applyThemeMode, athleteThemeStyle, type AthleteThemeSettings } from "@/lib/athleteTheme";
import type { ScheduledClass } from "@/types/types";

export type SchedulePreviewRole = "admin" | "coach" | "client";
export type SchedulePreviewView = "day" | "week" | "agenda";

// Illustrative sessions only: never fetched, booked, or persisted as business records.
const PREVIEW_DATE = new Date(2030, 0, 7, 12);
const WEEK = Array.from({ length: 7 }, (_, index) => addDays(PREVIEW_DATE, index));
const ignoreAction = () => {};

export function SchedulePreviewPanel({ theme, mode, role, view }: {
  theme: AthleteThemeSettings;
  mode: "light" | "dark";
  role: SchedulePreviewRole;
  view: SchedulePreviewView;
}) {
  const { t } = useTranslation(["settings", "schedule"]);
  const resolved = applyThemeMode(theme, mode);
  const classes: Array<ScheduledClass & { booked_count: number }> = WEEK.flatMap((date, day) => [0, 1, 2].map((index) => ({
    id: `preview-${day}-${index}`, class_date: format(date, "yyyy-MM-dd"),
    name: t("appearance.sampleSession", { ns: "settings", number: index + 1 }),
    category: t("appearance.sampleCategory", { ns: "settings" }), instructor: "",
    time: ["08:00", "08:45", "09:30"][index], duration: "45", capacity: 3,
    attendance: 0, booked_count: [0, 2, 3][index], spots_available: [3, 1, 0][index],
  })));
  const dayClasses = classes.filter((session) => session.class_date === format(PREVIEW_DATE, "yyyy-MM-dd"));
  const isBooked = (id: string) => role === "client" && id.endsWith("-1");

  return (
    <div data-testid={`schedule-preview-${mode}`} data-role={role} data-view={view}
      className={`schedule-preview athlete-shell schedule-theme ${mode} ${theme.variant === "dark-premium" ? "athlete-shell--vb20" : "athlete-shell--standard"} min-w-0 rounded-md border border-border bg-background p-3 text-foreground`}
      style={athleteThemeStyle(resolved)}>
      <h4 className="mb-3 text-sm font-semibold">{t(`appearance.preview${mode === "light" ? "Light" : "Dark"}`, { ns: "settings" })}</h4>
      <ScheduleDateNavigation label={view === "day" ? "07/01/2030" : "07 – 13/01/2030"} onPrevious={ignoreAction} onNext={ignoreAction} />
      <div className="mt-3" {...{ inert: "", "aria-label": t("appearance.sampleData", { ns: "settings" }) }}>
        {view === "week" ? (
          <WeeklyScheduleGrid weekDates={WEEK} selectedDate={PREVIEW_DATE} onDateSelect={ignoreAction} onAddClass={ignoreAction}
            onEditClass={ignoreAction} isAdmin={role !== "client"} isLoading={false} uniformMemberLayout
            getClassForTimeSlot={(date, time) => classes.find((session) => session.class_date === format(date, "yyyy-MM-dd") && session.time === time)}
            onHandleClassClick={ignoreAction} isClassBooked={isBooked} />
        ) : view === "agenda" ? (
          <WeeklyAgendaList weekDates={WEEK} classes={classes} onClassClick={ignoreAction} isClassBooked={isBooked} manageMode={role !== "client"} />
        ) : role === "client" ? (
          <AthleteDayList date={PREVIEW_DATE} classes={dayClasses} onClassClick={ignoreAction} isClassBooked={isBooked} />
        ) : (
          <div className="coach-day-view">
            <div className="coach-day-timeline relative h-56 overflow-hidden rounded-md border border-border">
              <div className="absolute inset-y-0 left-0 w-14 border-r border-border bg-muted/40">
                {dayClasses.map((session) => <div key={session.id} className="flex h-[65px] items-start justify-center pt-2 text-xs text-muted-foreground">{session.time}</div>)}
              </div>
              <div className="relative ml-14 h-full">
                {dayClasses.map((session) => <ScheduleTimelineCard key={session.id} klass={session} onOpenAttendees={ignoreAction} slotDuration={45} startTime="08:00" />)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function ScheduleAppearancePreview({ theme }: { theme: AthleteThemeSettings }) {
  const { t } = useTranslation(["settings", "schedule"]);
  const [role, setRole] = useState<SchedulePreviewRole>("admin");
  const [view, setView] = useState<SchedulePreviewView>("day");
  return (
    <section className="min-w-0 space-y-3" aria-label={t("appearance.schedulePreview")}>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-semibold">{t("appearance.schedulePreview")}</h3>
        <div className="flex flex-wrap items-center gap-2 [&_.schedule-view-switcher]:sm:min-w-56">
          <Select value={role} onValueChange={(value: SchedulePreviewRole) => setRole(value)}>
            <SelectTrigger className="w-36" aria-label={t("appearance.previewRole")}><SelectValue /></SelectTrigger>
            <SelectContent>{(["admin", "coach", "client"] as const).map((value) => <SelectItem key={value} value={value}>{t(`appearance.previewRole_${value}`)}</SelectItem>)}</SelectContent>
          </Select>
          <ScheduleViewSwitcher value={view} onChange={(value: SchedulePreviewView) => setView(value)} label={t("schedule:viewMode", "Affichage")}
            options={[{ value: "day" as const, label: t("schedule:dayView", "Jour"), icon: CalendarDays }, { value: "week" as const, label: t("schedule:weekView", "Semaine"), icon: LayoutGrid }, { value: "agenda" as const, label: t("schedule:agendaView", "Agenda"), icon: List }]} />
        </div>
      </div>
      <p className="text-xs text-muted-foreground">{t("appearance.sampleData")}</p>
      <div className="grid min-w-0 gap-4 xl:grid-cols-2">
        {(["light", "dark"] as const).map((mode) => <SchedulePreviewPanel key={mode} theme={theme} mode={mode} role={role} view={view} />)}
      </div>
    </section>
  );
}